import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../axiosInstance";
import { useAuth } from "../../contexts/AuthContext";
import InputField from "../../components/InputComponent/InputField";
import Pagination from "../../components/Pagination/Pagination";
import GeneralTable from "../../components/Table/GeneralTable";
import IconButton from "../../components/IconButton/IconButton";
import "./TimeSheet.scss";

const TimeSheet = () => {
  const { user } = useAuth();

  const [filters, setFilters] = useState({
    employee: "",
    role: "",
    department: "",
    status: "",
  });
  const companyId = parseInt(process.env.REACT_APP_COMPANY_ID);
  const [currentPage, setCurrentPage] = useState(1);
  const [timeSheetData, setTimeSheetData] = useState([]);
  const [showProjects, setShowProjects] = useState(false);
  const [workedDuration, setWorkedDuration] = useState(2);
  const [enteredHours, setEnteredHours] = useState(0);
  const [enteredMinutes, setEnteredMinutes] = useState(0);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [statuses, setStatuses] = useState([]);

  // Bulk selection state
  const [selectedTimesheets, setSelectedTimesheets] = useState(new Set());

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTimesheet, setEditingTimesheet] = useState(null);
  const [editedWorkTasks, setEditedWorkTasks] = useState([]);

  // Report view state
  const [viewReport, setViewReport] = useState(false);
  const [reportData, setReportData] = useState([]);

  // Role toggle states
  const [selectedRole, setSelectedRole] = useState("");
  const [availableRoles, setAvailableRoles] = useState([]);

  const dataPerPage = 10;

  // Role detection useEffect
  useEffect(() => {
    if (!user) return;

    const roles = [];
    if (user.authorities?.includes("ROLE_ADMIN")) roles.push("ADMIN");
    if (user.authorities?.includes("ROLE_HR")) roles.push("HR");
    if (user.authorities?.includes("ROLE_MANAGER")) roles.push("MANAGER");

    setAvailableRoles(roles);

    // Set default role (prioritize ADMIN > HR > MANAGER)
    if (roles.includes("ADMIN")) {
      setSelectedRole("ADMIN");
    } else if (roles.includes("HR")) {
      setSelectedRole("HR");
    } else if (roles.includes("MANAGER")) {
      setSelectedRole("MANAGER");
    }
  }, [user]);

  // Helper function to format duration in hours and minutes
  const formatDuration = useCallback((totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  }, []);

  // Helper function to convert hours and minutes to total minutes
  const convertToMinutes = useCallback((hours, minutes) => {
    return (hours || 0) * 60 + (minutes || 0);
  }, []);

  // Memoize validation function
  const validateInput = useCallback(
    (hours, minutes) => {
      const totalMinutes = convertToMinutes(hours, minutes);
      const maxAllowedMinutes = workedDuration * 60; // Convert hours to minutes
      return totalMinutes <= maxAllowedMinutes;
    },
    [workedDuration, convertToMinutes],
  );

  // Calculate total worked hours for a timesheet
  const calculateTotalWorkedHours = useCallback(
    (workTasks) => {
      if (!workTasks || workTasks.length === 0) return 0;

      return workTasks.reduce((total, task) => {
        const taskMinutes = convertToMinutes(
          task.workedHours,
          task.workedMinutes,
        );
        return total + taskMinutes;
      }, 0);
    },
    [convertToMinutes],
  );

  // Calculate total worked hours for all timesheets
  const totalWorkedHours = useMemo(() => {
    return timeSheetData.reduce((total, entry) => {
      const entryTotal = calculateTotalWorkedHours(entry.workTaskSheets);
      return total + entryTotal;
    }, 0);
  }, [timeSheetData, calculateTotalWorkedHours]);

  // Fetch worked duration
  useEffect(() => {
    const fetchWorkedDuration = async () => {
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_EMPLOYEE_URL}/timesheets/last-7-days`,
        );
        const duration = response.data.data;
        setWorkedDuration(duration);
      } catch (error) {
        console.error("Error fetching worked duration:", error);
      }
    };

    fetchWorkedDuration();
  }, []);

  const handleHoursChange = useCallback(
    (e) => {
      const hours = parseInt(e.target.value, 10) || 0;
      if (validateInput(hours, enteredMinutes)) {
        setEnteredHours(hours);
      } else {
        toast.error(
          `Entered time exceeds allowed duration of ${formatDuration(
            workedDuration * 60,
          )}.`,
        );
      }
    },
    [enteredMinutes, validateInput, workedDuration, formatDuration],
  );

  const handleMinutesChange = useCallback(
    (e) => {
      const minutes = parseInt(e.target.value, 10) || 0;
      if (validateInput(enteredHours, minutes)) {
        setEnteredMinutes(minutes);
      } else {
        toast.error(
          `Entered time exceeds allowed duration of ${formatDuration(
            workedDuration * 60,
          )}.`,
        );
      }
    },
    [enteredHours, validateInput, workedDuration, formatDuration],
  );

  // Fetch company settings
  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        const settingsResponse = await axiosInstance.get(
          `${process.env.REACT_APP_API_EMPLOYEE_URL}/company-settings/timesheet-Status`,
        );
        setShowProjects(settingsResponse.data.data);
      } catch (error) {
        console.error("Error fetching company settings:", error);
      }
    };

    fetchCompanySettings();
  }, [companyId]);

  // Fetch timesheet data
  const fetchTimeSheets = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_MANAGER_URL}/timesheets/pending`,
      );

      const data = response.data?.data || [];

      // Group by employee and date first
      const groupedData = {};

      data.forEach((sheet) => {
        const key = `${sheet.employeeName}-${sheet.timesheetDate}`;

        if (!groupedData[key]) {
          groupedData[key] = {
            empId: sheet.timeSheetId,
            employee: sheet.employeeName,
            date: sheet.timesheetDate,
            status: sheet.managerApproval,
            projects: [],
            workTaskSheets: [],
            totalWorkedMinutes: 0,
          };
        }

        // Add work tasks to the grouped entry
        if (sheet.workTaskSheets && sheet.workTaskSheets.length > 0) {
          sheet.workTaskSheets.forEach((task) => {
            groupedData[key].projects.push({
              workTaskId: task.workTaskId || task.id,
              name: task.projectName,
              task: task.task,
              workedHours: task.workedHours,
              workedMinutes: task.workedMinutes,
              totalHours: task.workedHours + task.workedMinutes / 60,
            });

            groupedData[key].workTaskSheets.push(task);
          });
        }
      });

      // Convert back to array and calculate total worked minutes
      const formattedData = Object.values(groupedData).map((entry) => ({
        ...entry,
        totalWorkedMinutes: calculateTotalWorkedHours(entry.workTaskSheets),
      }));

      setTimeSheetData(formattedData);
      setSelectedTimesheets(new Set()); // Clear selection when data loads
    } catch (error) {
      console.error("Error fetching timesheet data:", error);
    }
  }, [calculateTotalWorkedHours]);

  // Fetch complete report
  const fetchCompleteReport = useCallback(
    async (roleOverride = null) => {
      const roleToUse = roleOverride || selectedRole;

      if (!roleToUse) return;

      try {
        let endpoint;

        // Determine endpoint based on selected role
        switch (roleToUse) {
          case "ADMIN":
            endpoint = `${process.env.REACT_APP_API_BASE_URL}/timesheets/All`;
            break;
          case "HR":
            endpoint = `${process.env.REACT_APP_API_HR_URL}/timesheets/All`;
            break;
          case "MANAGER":
            endpoint = `${process.env.REACT_APP_API_MANAGER_URL}/timesheets/All`;
            break;
          default:
            endpoint = `${process.env.REACT_APP_API_MANAGER_URL}/timesheets/All`;
        }

        const response = await axiosInstance.get(endpoint);
        const data = response.data?.data || [];

        // Group by employee and date first
        const groupedData = {};

        data.forEach((sheet) => {
          const key = `${sheet.employeeName}-${sheet.timesheetDate}`;

          if (!groupedData[key]) {
            groupedData[key] = {
              empId: sheet.timeSheetId,
              employee: sheet.employeeName,
              date: sheet.timesheetDate,
              status: sheet.managerApproval,
              workedDuration: sheet.workedDuration,
              projects: [],
              workTaskSheets: [],
              totalWorkedMinutes: 0,
            };
          }

          // Add work tasks to the grouped entry
          if (sheet.workTaskSheets && sheet.workTaskSheets.length > 0) {
            sheet.workTaskSheets.forEach((task) => {
              groupedData[key].projects.push({
                workTaskId: task.workTaskId || task.id,
                name: task.projectName,
                task: task.task,
                workedHours: task.workedHours,
                workedMinutes: task.workedMinutes,
                totalHours: task.workedHours + task.workedMinutes / 60,
              });

              groupedData[key].workTaskSheets.push(task);
            });
          }
        });

        // Convert back to array and calculate total worked minutes
        const formattedData = Object.values(groupedData).map((entry) => ({
          ...entry,
          totalWorkedMinutes: calculateTotalWorkedHours(entry.workTaskSheets),
        }));

        setReportData(formattedData);
      } catch (error) {
        console.error("Error fetching complete report:", error);
        toast.error("Failed to fetch complete timesheet report");
        setReportData([]);
      }
    },
    [selectedRole, calculateTotalWorkedHours],
  );

  // Role switching handler
  const handleRoleSwitch = useCallback(
    async (role) => {
      if (role === selectedRole) return;

      setSelectedRole(role);

      // Fetch new data for the selected role
      await fetchCompleteReport(role);
    },
    [selectedRole, fetchCompleteReport],
  );

  // Handle view report toggle
  const handleViewReport = useCallback(async () => {
    if (!viewReport) {
      await fetchCompleteReport();
    }
    setViewReport(!viewReport);
  }, [viewReport, fetchCompleteReport]);

  useEffect(() => {
    fetchTimeSheets();
  }, [fetchTimeSheets, showProjects]);

  // Group timesheets by employee and date
  const groupTimesheetsByEmployeeAndDate = useCallback((data) => {
    const grouped = {};

    data.forEach((entry) => {
      const key = `${entry.employee}-${entry.date}`;

      if (!grouped[key]) {
        grouped[key] = {
          empId: entry.empId,
          employee: entry.employee,
          date: entry.date,
          status: entry.status,
          projects: [],
          workTaskSheets: [],
          totalWorkedMinutes: 0,
        };
      }

      // Combine projects and work task sheets
      grouped[key].projects.push(...entry.projects);
      grouped[key].workTaskSheets.push(...entry.workTaskSheets);
      grouped[key].totalWorkedMinutes += entry.totalWorkedMinutes;
    });

    return Object.values(grouped);
  }, []);

  // Bulk selection handlers
  const handleToggleSelect = useCallback((empId) => {
    setSelectedTimesheets((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(empId)) {
        newSet.delete(empId);
      } else {
        newSet.add(empId);
      }
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedTimesheets.size === timeSheetData.length) {
      setSelectedTimesheets(new Set());
    } else {
      setSelectedTimesheets(new Set(timeSheetData.map((item) => item.empId)));
    }
  }, [selectedTimesheets.size, timeSheetData]);

  // Bulk approve
  const handleBulkApprove = useCallback(async () => {
    if (selectedTimesheets.size === 0) {
      toast.warning("Please select at least one timesheet");
      return;
    }

    const payload = timeSheetData
      .filter((item) => selectedTimesheets.has(item.empId))
      .map((item) => ({
        timeSheetId: item.empId,
        managerApproval: "APPROVED",
        workTaskSheets: item.workTaskSheets || [],
      }));

    try {
      await axiosInstance.put(
        `${process.env.REACT_APP_API_MANAGER_URL}/timesheets/manager-approval/bulk`,
        payload,
      );

      toast.success(`Approved ${payload.length} timesheet(s)`);
      setSelectedTimesheets(new Set());
      fetchTimeSheets();
    } catch (error) {
      console.error("Bulk approval failed:", error);
      toast.error("Bulk timesheet approval failed");
    }
  }, [selectedTimesheets, timeSheetData, fetchTimeSheets]);

  // Bulk reject
  const handleBulkReject = useCallback(async () => {
    if (selectedTimesheets.size === 0) {
      toast.warning("Please select at least one timesheet");
      return;
    }

    const payload = timeSheetData
      .filter((item) => selectedTimesheets.has(item.empId))
      .map((item) => ({
        timeSheetId: item.empId,
        managerApproval: "REJECTED",
        workTaskSheets: item.workTaskSheets || [],
      }));

    try {
      await axiosInstance.put(
        `${process.env.REACT_APP_API_MANAGER_URL}/timesheets/manager-reject/bulk`,
        payload,
      );

      toast.success(`Rejected ${payload.length} timesheet(s)`);
      setSelectedTimesheets(new Set());
      fetchTimeSheets();
    } catch (error) {
      console.error("Bulk reject failed:", error);
      toast.error("Bulk timesheet rejection failed");
    }
  }, [selectedTimesheets, timeSheetData, fetchTimeSheets]);

  // Edit functionality
  const handleEditClick = useCallback((timesheet) => {
    setEditingTimesheet(timesheet);
    setEditedWorkTasks(
      timesheet.workTaskSheets.map((task) => ({
        workTaskId: task.workTaskId,
        projectName: task.projectName,
        task: task.task,
        workedHours: task.workedHours,
        workedMinutes: task.workedMinutes,
      })),
    );
    setShowEditModal(true);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setShowEditModal(false);
    setEditingTimesheet(null);
    setEditedWorkTasks([]);
  }, []);

  // OPTIMIZED: Use useCallback to prevent re-renders
  const handleWorkTaskChange = useCallback((index, field, value) => {
    setEditedWorkTasks((prevTasks) => {
      // Only update if the value actually changed
      const currentValue = prevTasks[index][field];
      const newValue = parseInt(value) || 0;

      if (currentValue === newValue) {
        return prevTasks; // Return same reference if no change
      }

      // Create new array only when needed
      const updatedTasks = [...prevTasks];
      updatedTasks[index] = {
        ...updatedTasks[index],
        [field]: newValue,
      };
      return updatedTasks;
    });
  }, []);

  const handleSaveEdit = useCallback(async () => {
    try {
      // Validate all tasks
      for (const task of editedWorkTasks) {
        if (!validateInput(task.workedHours, task.workedMinutes)) {
          toast.error(
            `Entered time for ${
              task.projectName
            } exceeds allowed duration of ${formatDuration(
              workedDuration * 60,
            )}.`,
          );
          return;
        }
      }

      const response = await axiosInstance.put(
        `${process.env.REACT_APP_API_MANAGER_URL}/timesheets/manager-approval`,
        {
          timeSheetId: editingTimesheet.empId,
          managerApproval: editingTimesheet.status,
          workTaskSheets: editedWorkTasks,
        },
      );

      if (response.status === 200) {
        toast.success("Worked hours updated successfully!");
        handleCloseEditModal();
        fetchTimeSheets();
      }
    } catch (error) {
      console.error("Error updating worked hours:", error);
      toast.error("Failed to update worked hours.");
    }
  }, [
    editedWorkTasks,
    editingTimesheet,
    validateInput,
    handleCloseEditModal,
    fetchTimeSheets,
    workedDuration,
    formatDuration,
  ]);

  // Memoize filtered data
  const filteredData = useMemo(() => {
    return timeSheetData.filter((entry) => {
      const matchesEmployee = filters.employee
        ? entry.employee?.toLowerCase().includes(filters.employee.toLowerCase())
        : true;
      const matchesStatus = filters.status
        ? entry.status === filters.status
        : true;

      return matchesEmployee && matchesStatus;
    });
  }, [timeSheetData, filters.employee, filters.status]);

  // Memoize filtered report data
  const filteredReportData = useMemo(() => {
    return reportData.filter((entry) => {
      const matchesEmployee = filters.employee
        ? entry.employee?.toLowerCase().includes(filters.employee.toLowerCase())
        : true;
      const matchesStatus = filters.status
        ? entry.status === filters.status
        : true;

      return matchesEmployee && matchesStatus;
    });
  }, [reportData, filters.employee, filters.status]);

  // Memoize columns
  const columns = useMemo(() => {
    const baseColumns = [];

    // Add select column only in pending view (not in report view)
    if (!viewReport) {
      baseColumns.push({
        header: "Select",
        accessor: "select",
      });
    }

    baseColumns.push(
      {
        header: "Employee",
        accessor: "employee",
      },
      { header: "Date", accessor: "date" },
    );

    if (showProjects) {
      baseColumns.push(
        { header: "Project", accessor: "project" },
        { header: "Worked Hrs", accessor: "workedHours" },
      );
    }

    baseColumns.push(
      { header: "Total Hours", accessor: "totalHours" },
      { header: "Status", accessor: "status" },
    );

    if (!viewReport) {
      baseColumns.push({
        header: "Actions",
        accessor: "actions",
      });
    }

    return baseColumns;
  }, [showProjects, viewReport]);

  // Approval and rejection handling
  const handleApproval = useCallback(
    async (timeSheetId) => {
      try {
        const response = await axiosInstance.put(
          `${process.env.REACT_APP_API_MANAGER_URL}/timesheets/manager-approval`,
          { timeSheetId, managerApproval: "APPROVED" },
        );

        if (response.status === 200) {
          toast.success("Timesheet approved successfully!");
          fetchTimeSheets();
        }
      } catch (error) {
        console.error("Error approving timesheet:", error);
        toast.error("Failed to approve timesheet.");
      }
    },
    [fetchTimeSheets],
  );

  const handleReject = useCallback(
    async (timeSheetId) => {
      try {
        const response = await axiosInstance.put(
          `${process.env.REACT_APP_API_MANAGER_URL}/timesheets/manager-reject`,
          { timeSheetId, managerApproval: "REJECTED" },
        );

        if (response.status === 200) {
          toast.success("Timesheet rejected successfully!");
          fetchTimeSheets();
        }
      } catch (error) {
        console.error("Error rejecting timesheet:", error);
        toast.error("Failed to reject timesheet.");
      }
    },
    [fetchTimeSheets],
  );

  const prepareTableData = useCallback(
    (data) => {
      const tableData = [];

      data.forEach((entry) => {
        const actionButtons = !viewReport ? (
          <div className="action-buttons">
            <IconButton
              className="icon-button--edit"
              icon="mdi:pencil"
              onClick={() => handleEditClick(entry)}
            />
            <IconButton
              className="icon-button--add"
              icon="mdi:tick"
              onClick={() => handleApproval(entry.empId)}
            />
            <IconButton
              className="icon-button--delete"
              icon="icons8:cancel-2"
              onClick={() => handleReject(entry.empId)}
            />
          </div>
        ) : null;

        const selectCheckbox = !viewReport ? (
          <input
            type="checkbox"
            checked={selectedTimesheets.has(entry.empId)}
            onChange={() => handleToggleSelect(entry.empId)}
            style={{ cursor: "pointer", width: "18px", height: "18px" }}
          />
        ) : null;

        if (showProjects && entry.projects.length > 0) {
          // Show all projects in a single row, comma-separated
          const projectsDisplay = entry.projects
            .map(
              (project) =>
                `${project.name} (${project.totalHours.toFixed(2)}h)`,
            )
            .join(", ");

          tableData.push({
            empId: entry.empId,
            select: selectCheckbox,
            employee: entry.employee,
            date: entry.date,
            project: projectsDisplay,
            workedHours: entry.projects
              .reduce((sum, project) => sum + project.totalHours, 0)
              .toFixed(2),
            totalHours: formatDuration(entry.totalWorkedMinutes),
            status: entry.status,
            actions: actionButtons,
            isFirstRow: true,
            totalProjects: entry.projects.length,
          });
        } else {
          tableData.push({
            empId: entry.empId,
            select: selectCheckbox,
            employee: entry.employee,
            date: entry.date,
            totalHours: formatDuration(entry.totalWorkedMinutes),
            status: entry.status,
            actions: actionButtons,
            isFirstRow: true,
            totalProjects: 1,
          });
        }
      });

      return tableData;
    },
    [
      showProjects,
      handleEditClick,
      formatDuration,
      viewReport,
      selectedTimesheets,
      handleToggleSelect,
    ],
  );

  // Memoize table data and pagination
  const currentDataSource = viewReport ? filteredReportData : filteredData;
  const tableData = useMemo(
    () => prepareTableData(currentDataSource),
    [prepareTableData, currentDataSource],
  );
  const indexOfLastData = currentPage * dataPerPage;
  const indexOfFirstData = indexOfLastData - dataPerPage;
  const currentData = useMemo(
    () => tableData.slice(indexOfFirstData, indexOfLastData),
    [tableData, indexOfFirstData, indexOfLastData],
  );
  const totalPages = Math.ceil(tableData.length / dataPerPage);

  // Enhanced TaskRow validation
  const validateTaskInput = useCallback(
    (task) => {
      const taskMinutes = convertToMinutes(
        task.workedHours,
        task.workedMinutes,
      );
      const maxAllowedMinutes = workedDuration * 60;
      return {
        isValid: taskMinutes <= maxAllowedMinutes,
        currentMinutes: taskMinutes,
        maxMinutes: maxAllowedMinutes,
      };
    },
    [convertToMinutes, workedDuration],
  );

  // OPTIMIZED: Memoize the EditModal component
  const EditModal = useMemo(() => {
    if (!showEditModal || !editingTimesheet) return null;

    const totalEditedMinutes = calculateTotalWorkedHours(editedWorkTasks);

    return (
      <div className="modal-overlay">
        <div className="edit-modal">
          <div className="modal-header">
            <h3>Edit Timesheet - {editingTimesheet.employee}</h3>
            <button className="close-btn" onClick={handleCloseEditModal}>
              ×
            </button>
          </div>

          <div className="modal-body">
            <div className="timesheet-info">
              <p>
                <strong>Date:</strong> {editingTimesheet.date}
              </p>
              <p>
                <strong>Status:</strong> {editingTimesheet.status}
              </p>
              <p>
                <strong>Total Worked Hours:</strong>{" "}
                {formatDuration(totalEditedMinutes)}
              </p>
              <p>
                <strong>Allowed Duration:</strong>{" "}
                {formatDuration(workedDuration * 60)}
              </p>
            </div>

            <div className="work-tasks">
              <h4>Work Tasks</h4>
              {editedWorkTasks.map((task, index) => (
                <TaskRow
                  key={`${task.workTaskId}-${index}`}
                  task={task}
                  index={index}
                  onTaskChange={handleWorkTaskChange}
                  validateTask={validateTaskInput}
                  formatDuration={formatDuration}
                />
              ))}
            </div>
          </div>

          <div className="modal-footer">
            <button className="btn btn-cancel" onClick={handleCloseEditModal}>
              Cancel
            </button>
            <button className="btn btn-save" onClick={handleSaveEdit}>
              Save Changes
            </button>
          </div>
        </div>
      </div>
    );
  }, [
    showEditModal,
    editingTimesheet,
    editedWorkTasks,
    handleCloseEditModal,
    handleSaveEdit,
    handleWorkTaskChange,
    calculateTotalWorkedHours,
    formatDuration,
    workedDuration,
    validateTaskInput,
  ]);

  return (
    <div className="timesheet-container">
      <GeneralTable
        title={viewReport ? "Timesheet Report" : "Team Time Sheet"}
        columns={columns}
        data={currentData}
        titleActions={
          <div className="team-page-toolbar-actions">
            <button
              className={`control-button ${viewReport ? "active" : ""}`}
              onClick={handleViewReport}
              disabled={!selectedRole}
            >
              <i className="icon-report"></i>
              {viewReport ? "Close report" : "Complete report"}
            </button>
            {viewReport && availableRoles.length > 1 &&
              availableRoles.map((role) => (
                <button
                  key={role}
                  className={`role-toggle-btn ${selectedRole === role ? "active" : ""}`}
                  onClick={() => handleRoleSwitch(role)}
                >
                  {role === "ADMIN" ? "Admin" : role === "HR" ? "HR" : "Manager"}
                </button>
              ))}
            {!viewReport && timeSheetData.length > 0 && (
              <>
                <button className="hr-btn hr-btn--secondary" onClick={handleSelectAll}>
                  {selectedTimesheets.size === timeSheetData.length ? "Deselect all" : "Select all"}
                </button>
                {selectedTimesheets.size > 0 && (
                  <>
                    <span className="team-selection-count">{selectedTimesheets.size} selected</span>
                    <IconButton className="icon-button--add" icon="mdi:check-all" onClick={handleBulkApprove}>
                      Approve ({selectedTimesheets.size})
                    </IconButton>
                    <IconButton className="icon-button--delete" icon="mdi:close-thick" onClick={handleBulkReject}>
                      Reject ({selectedTimesheets.size})
                    </IconButton>
                  </>
                )}
              </>
            )}
          </div>
        }
      />

      {tableData.length > dataPerPage && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      {EditModal}
    </div>
  );
};

// OPTIMIZED: Extract TaskRow as a separate memoized component with enhanced validation
const TaskRow = React.memo(
  ({ task, index, onTaskChange, validateTask, formatDuration }) => {
    const handleHoursChange = useCallback(
      (e) => {
        const newHours = parseInt(e.target.value, 10) || 0;
        const validation = validateTask({
          ...task,
          workedHours: newHours,
        });

        if (!validation.isValid) {
          toast.error(
            `Entered time for ${
              task.projectName
            } exceeds allowed duration of ${formatDuration(
              validation.maxMinutes,
            )}.`,
          );
          return;
        }

        onTaskChange(index, "workedHours", e.target.value);
      },
      [index, onTaskChange, task, validateTask, formatDuration],
    );

    const handleMinutesChange = useCallback(
      (e) => {
        const newMinutes = parseInt(e.target.value, 10) || 0;
        const validation = validateTask({
          ...task,
          workedMinutes: newMinutes,
        });

        if (!validation.isValid) {
          toast.error(
            `Entered time for ${
              task.projectName
            } exceeds allowed duration of ${formatDuration(
              validation.maxMinutes,
            )}.`,
          );
          return;
        }

        onTaskChange(index, "workedMinutes", e.target.value);
      },
      [index, onTaskChange, task, validateTask, formatDuration],
    );

    const taskTotalMinutes =
      (task.workedHours || 0) * 60 + (task.workedMinutes || 0);

    return (
      <div className="task-row">
        <div className="task-info">
          <label>
            <strong>{task.projectName}</strong>
          </label>
          <p>Task: {task.task}</p>
          <p className="task-total">
            Total: {formatDuration(taskTotalMinutes)}
          </p>
        </div>

        <div className="time-inputs">
          <div className="input-group">
            <label>Hours:</label>
            <input
              type="number"
              min="0"
              max="23"
              value={task.workedHours}
              onChange={handleHoursChange}
              className="time-input"
            />
          </div>

          <div className="input-group">
            <label>Minutes:</label>
            <input
              type="number"
              min="0"
              max="59"
              value={task.workedMinutes}
              onChange={handleMinutesChange}
              className="time-input"
            />
          </div>
        </div>
      </div>
    );
  },
);

TaskRow.displayName = "TaskRow";

export default TimeSheet;
